// src/features/StrategyPermissions/new.tsx

import { Form } from "@/components/ui/form";
import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { Diff } from "@/features/FormCard/Diff";
import {
  AutocompleteAsyncField,
  SelectField,
} from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { levelOptions } from "./filter";

const FormSchema = z.object({
  user_email: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          email: z.string(),
        }),
      ])
      .optional(),
  ),
  user_id: z.coerce.number().min(1, "User is required"),
  strategy_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      ])
      .optional(),
  ),
  strategy_id: z.coerce.number().min(1, "Strategy is required"),
  level: z.coerce.number().min(1, "Level is required"),
});

export function StrategyPermissionsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                StrategyPermissions {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="user_email"
                    label={t(`${table_name}.columns.user_email`, {
                      ns: "tables",
                      defaultValue: "User",
                    })}
                    base_url="legacy-quantilia/users"
                    column_name="email"
                    id_column="user_id"
                    control={form.control}
                    isRequired
                  />
                  <AutocompleteAsyncField
                    name="strategy_name"
                    label={t(`${table_name}.columns.email`, {
                      ns: "tables",
                      defaultValue: "Strategy",
                    })}
                    base_url="legacy-quantilia/strategies"
                    column_name="name"
                    id_column="strategy_id"
                    control={form.control}
                    isRequired
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="level"
                    label={t(`${table_name}.columns.level`, {
                      ns: "tables",
                      defaultValue: "Level",
                    })}
                    options={levelOptions}
                    control={form.control}
                    isRequired
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["user_id", "strategy_id"])
              : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
